import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./FeedActions.scss";

import { getCurrentUserId, getAccessToken } from "../../awsAuth";
import ReportsModal from "../Reports/Reports";
import Alerts from "../Alerts/Alerts";
import { useToast } from "../Alerts/Toast";

import statisticsIcon from "./img/statistics.png";
import raiseIcon from "./img/raise.png";
import archiveIcon from "./img/archive.png";
import editIcon from "./img/edit.png";
import deleteIcon from "./img/delete.png";
import notInterestingIcon from "./img/notInteresting.png";
import reportIcon from "./img/report.png";
import messagesIcon from "./img/messages.png";

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API ||
  "http://localhost:5000";

const REASONS = [
  { code: "18_PLUS", label: "18+ контент" },
  { code: "VIOLENCE", label: "Насильство/жорстокість" },
  { code: "HATE", label: "Мова ворожнечі/пропаганда" },
  { code: "SCAM", label: "Шахрайство/маніпуляції" },
  { code: "SPAM", label: "Спам/масова реклама" },
  { code: "ILLEGAL", label: "Незаконний товар/послуга" },
  { code: "OTHER", label: "Інше (порушення правил спільноти)" },
];

const apiUrl = (path) => {
  if (!path) return String(API || "");
  if (/^https?:\/\//i.test(path)) return path;

  const base = String(API || "").replace(/\/+$/, "");
  const p = String(path || "");
  return p.startsWith("/") ? `${base}${p}` : `${base}/${p}`;
};

function useScrollLock(active) {
  useEffect(() => {
    if (!active) return;

    const body = document.body;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollbarW = Math.max(
      0,
      window.innerWidth - document.documentElement.clientWidth
    );

    body.classList.add("cv-modal-open");
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;

    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.paddingRight = prev.paddingRight;
      body.classList.remove("cv-modal-open");
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

function ReasonModal({ open, onClose, onSubmit }) {
  const [reason, setReason] = useState("OTHER");
  const [note, setNote] = useState("");

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setReason("OTHER");
    setNote("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="fa__portal" onClick={onClose}>
      <div className="fa__sheet" onClick={(e) => e.stopPropagation()}>
        <h3 className="fa__sheet-title">Причина видалення</h3>

        <div className="fa__field">
          <label>Оберіть причину</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="fa__input"
          >
            {REASONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div className="fa__field">
          <label>Коротке пояснення для автора (необов’язково)</label>
          <textarea
            className="fa__input"
            rows={3}
            placeholder="Напр.: містить відверті сцени / реклама гемблінгу тощо"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="fa__actions end">
          <button className="fa__btn ghost" onClick={onClose}>
            Скасувати
          </button>
          <button
            className="fa__btn danger"
            onClick={() => onSubmit({ reason, note })}
          >
            Підтвердити видалення
          </button>
        </div>
      </div>
    </div>
  );
}

function extractKeywords(text, max = 5) {
  if (!text) return [];

  const STOP = new Set([
    "і",
    "й",
    "та",
    "але",
    "або",
    "що",
    "це",
    "цей",
    "ця",
    "це",
    "ті",
    "та",
    "для",
    "без",
    "про",
    "над",
    "під",
    "між",
    "як",
    "у",
    "в",
    "на",
    "до",
    "по",
    "з",
    "із",
    "від",
    "за",
    "не",
    "ні",
    "тощо",
    "ж",
    "би",
    "де",
    "так",
    "ще",
    "вже",
    "я",
    "ти",
    "він",
    "вона",
    "ми",
    "ви",
    "вони",
    "його",
    "її",
    "їх",
    "мене",
    "мені",
    "тебе",
    "и",
    "но",
    "а",
    "в",
    "на",
    "по",
    "с",
    "со",
    "к",
    "до",
    "из",
    "у",
    "о",
    "об",
    "же",
    "ли",
    "не",
    "ни",
    "это",
    "the",
    "a",
    "an",
    "of",
    "for",
    "to",
    "in",
    "on",
    "with",
    "and",
    "or",
    "but",
    "is",
    "are",
    "be",
    "was",
    "were",
    "this",
    "that",
  ]);

  const tokens = String(text)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s#@]/gu, " ")
    .split(/\s+/)
    .filter((t) => t && t.length >= 3 && !STOP.has(t))
    .slice(0, 50);

  const freq = {};
  for (const t of tokens) freq[t] = (freq[t] || 0) + 1;

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([t]) => t);
}

function fmtMoney(minor) {
  if (typeof minor !== "number") return "—";
  return (minor / 100).toFixed(2) + " ₴";
}

function timeLeft(iso) {
  if (!iso) return "—";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = t - now;

  if (diff <= 0) return "завершено";

  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);

  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return `${d} дн ${rh} год`;
  }

  return `${h} год ${m} хв`;
}

export default function FeedActions({
  type,
  listingType,
  id,
  createdAt,
  isOwner,
  onClose,
  onDelete,
  authorId,
  authorName,
  postText,
  postUrl,
  category,
  onHideCard,
  raisedUntil,
  lastRaisedAt,
}) {
  const navigate = useNavigate();
  const uid = getCurrentUserId?.();
  const toast = useToast();

  const [busy, setBusy] = useState(false);

  const [isStaff, setIsStaff] = useState(false);
  const [staffRole, setStaffRole] = useState(null);

  const [isLeaving, setIsLeaving] = useState(false);
  const leaveTimerRef = useRef(null);

  const [reportOpen, setReportOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmVariant, setConfirmVariant] = useState("archiveListing");
  const [authAlertOpen, setAuthAlertOpen] = useState(false);
  const pendingActionRef = useRef(null);

  const EXIT_MS = 220;

  useScrollLock(
    !reportOpen &&
      !reasonOpen &&
      !authAlertOpen &&
      !confirmOpen
  );

  const isRaisedNow = useMemo(() => {
    const until = raisedUntil;
    if (!until) return false;
    const t = new Date(until).getTime();
    return Number.isFinite(t) && t > Date.now();
  }, [raisedUntil]);

  const entityType = useMemo(() => {
    return type === "post" ? "post" : "listing";
  }, [type]);

  const startLeave = useCallback(() => {
    if (isLeaving) return;

    setIsLeaving(true);

    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setIsLeaving(false);
      onClose?.();
    }, EXIT_MS);
  }, [isLeaving, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") startLeave();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [startLeave]);

  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (!uid) return;

        try {
          const token = await getAccessToken();
          if (!token) throw new Error("NO_TOKEN");

          const r = await axios.get(apiUrl(`/api/admin/me`), {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const d = r?.data?.data || {};
          if (!alive) return;

          const staff = !!(d.isAdmin || d.isMod);
          setIsStaff(staff);
          setStaffRole(d.isAdmin ? "admin" : d.isMod ? "mod" : null);
          if (staff) return;
        } catch {}

        try {
          const r2 = await axios.get(
            apiUrl(`/api/account/${encodeURIComponent(uid)}`)
          );
          const u = r2?.data || {};
          if (!alive) return;

          const staff = !!(u.isAdmin || u.isMod);
          setIsStaff(staff);
          setStaffRole(u.isAdmin ? "admin" : u.isMod ? "mod" : null);
        } catch {
          if (!alive) return;
          setIsStaff(false);
          setStaffRole(null);
        }
      } catch {
        if (!alive) return;
        setIsStaff(false);
        setStaffRole(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [uid]);

  const showStaffForThisCard = useMemo(
    () => isStaff && (type === "listing" || type === "post") && !isOwner,
    [isStaff, type, isOwner]
  );

  const sendSignal = useCallback(
    async (event, extra = {}) => {
      try {
        await axios.post(apiUrl(`/api/signals`), {
          userId: uid || "guest",
          listingId: String(id),
          authorId: authorId || null,
          category: extra.category ?? category ?? null,
          event,
          dwellMs: extra.dwellMs || 0,
          keywords: extra.keywords || null,
        });
      } catch (e) {
        console.error("signals error", e);
      }
    },
    [uid, id, authorId, category]
  );

  const handleArchive = async () => {
    if (busy) return;

    pendingActionRef.current = "archive";
    setConfirmVariant("archiveListing");
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (busy) return;

    pendingActionRef.current = "delete";
    setConfirmVariant("deleteListing");
    setConfirmOpen(true);
  };

  const runConfirmedAction = async () => {
    const action = pendingActionRef.current;
    setConfirmOpen(false);

    if (!action) return;

    try {
      setBusy(true);

      const token = await getAccessToken();

      if (!token) {
        toast.error("Сесія недійсна. Увійдіть повторно.", 3200);
        return;
      }

      if (action === "archive") {
        if (entityType === "listing") {
          await axios.patch(
            apiUrl(`/api/listings/${encodeURIComponent(String(id))}/archive`),
            { createdAt },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } else {
          await axios.patch(
            apiUrl(`/api/posts/${encodeURIComponent(String(id))}/archive`),
            { createdAt },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        }

        toast.success("Переміщено в архів");

        if (typeof onHideCard === "function") {
          onHideCard(type, id);
        } else if (typeof onDelete === "function") {
          onDelete(type, id, createdAt);
        }

        startLeave();
        return;
      }

      if (action === "delete") {
        if (entityType === "listing") {
          const userId = uid;
          if (!userId) {
            toast.info("Спочатку увійдіть у свій акаунт", 2600);
            return;
          }

          await axios.delete(
            apiUrl(`/api/listings/${encodeURIComponent(String(id))}`),
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              data: { userId, createdAt },
            }
          );
        } else {
          await axios.delete(
            apiUrl(`/api/posts/${encodeURIComponent(String(id))}`),
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              data: { createdAt },
            }
          );
        }

        onDelete?.(type, id, createdAt);
        startLeave();
        return;
      }
    } catch (err) {
      console.error(
        action === "archive" ? "❌ Помилка архівування:" : "❌ Помилка видалення:",
        err
      );

      if (action === "archive") {
        toast.error("Не вдалося архівувати", 3200);
      } else {
        const msg = err?.response?.data?.error || "Не вдалося видалити";
        toast.error(String(msg || "Не вдалося виконати дію"), 3200);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = () => {
  if (!id) return;

  if (entityType === "listing") {
    if (listingType === "wanted") {
      navigate(`/edit/search/${encodeURIComponent(String(id))}`);
    } else {
      navigate(`/edit/listing/${encodeURIComponent(String(id))}`);
    }
  } else {
    navigate(`/edit/post/${encodeURIComponent(String(id))}`);
  }

  startLeave();
};

  const handleWriteAuthor = () => {
    if (!authorId) return;

    navigate(`/messages?to=${encodeURIComponent(authorId)}`);
    startLeave();
  };

  const handleNotInteresting = async () => {
    if (busy) return;

    setBusy(true);
    try {
      const baseText = `${postText || ""}`;
      const kws = extractKeywords(baseText, 5);

      await sendSignal("not_interesting", { keywords: kws });

      if (typeof onHideCard === "function") onHideCard(type, id);
      onDelete?.(type, id, createdAt);

      startLeave();
    } finally {
      setBusy(false);
    }
  };

  const openBoost = () => {
    if (busy) return;

    startLeave();

    setTimeout(() => {
      navigate(
        `/settings/promotion/boost?listingId=${encodeURIComponent(String(id))}`
      );
    }, EXIT_MS + 30);
  };

  const submitModeration = async ({ reason, note }) => {
    try {
      setBusy(true);

      if (!uid) {
        toast.error("Спочатку увійдіть у свій акаунт.", 3200);
        return;
      }

      const token = await getAccessToken();
      if (!token) {
        toast.error("Сесія недійсна. Увійдіть повторно.", 3200);
        return;
      }

      const target = entityType === "listing" ? "listings" : "posts";
      const url = apiUrl(
        `/api/admin/mod/${target}/${encodeURIComponent(String(id))}/remove`
      );

      const res = await axios.post(
        url,
        { reason: String(reason || "OTHER").toUpperCase(), message: note || "" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res?.data?.ok) {
        const reasonText =
          REASONS.find((r) => r.code === String(reason).toUpperCase())?.label ||
          "порушення правил спільноти";

        toast.success(
          `Видалено модератором: ${reasonText}. Автор отримає сповіщення із поясненням.`,
          3200
        );

        setReasonOpen(false);
        onDelete?.(type, id, createdAt);
        startLeave();
        return;
      }

      throw new Error(res?.data?.error || "REMOVE_FAILED");
    } catch (err) {
      console.error("❌ Помилка модерації:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Не вдалося виконати модерацію";
      alert(`⚠️ ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const stateClass = isLeaving ? "is-leaving" : "is-open";

  return (
    <>
      <div
        className={`fa__backdrop ${stateClass}`}
        onClick={startLeave}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`fa__card is-glass ${stateClass}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="fa__title">Дії</div>

          <div className={`fa__list staggered ${stateClass}`}>
            {isOwner ? (
              <>
                {/*
<button className="fa__item" disabled={busy}>
  <img
    src={statisticsIcon}
    alt=""
    className="fa__ico fa__ico--stats"
  />
  <span>Статистика</span>
</button>

{type === "listing" && (
  <button
    className="fa__item primary"
    onClick={openBoost}
    disabled={busy}
    title="Підняти оголошення в стрічці"
  >
    <img src={raiseIcon} alt="" className="fa__ico" />
    <span className="fa__txt">
      <span className="fa__txtMain">
        {isRaisedNow ? "Оголошення підняте" : "Підняти оголошення"}
      </span>

      {isRaisedNow && raisedUntil && (
        <span className="fa__txtSub">
          залишилось {timeLeft(raisedUntil)}
        </span>
      )}
    </span>
  </button>
)}
*/}

                <button
                  className="fa__item"
                  onClick={handleArchive}
                  disabled={busy}
                >
                  <img src={archiveIcon} alt="" className="fa__ico" />
                  <span>Архівувати</span>
                </button>

                <button className="fa__item" onClick={handleEdit} disabled={busy}>
                  <img src={editIcon} alt="" className="fa__ico" />
                  <span>Редагувати</span>
                </button>

                <button
                  className="fa__item danger"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  <img src={deleteIcon} alt="" className="fa__ico" />
                  <span>Видалити</span>
                </button>
              </>
            ) : (
              <>
                <button
                  className="fa__item"
                  disabled={busy || !authorId}
                  onClick={handleWriteAuthor}
                  title="Відкрити чат з автором"
                >
                  <img src={messagesIcon} alt="" className="fa__ico" />
                  <span>Написати автору</span>
                </button>

                <button
                  className="fa__item"
                  onClick={handleNotInteresting}
                  disabled={busy}
                  title="Не показувати подібне частіше"
                >
                  <img src={notInterestingIcon} alt="" className="fa__ico" />
                  <span>Не цікаво</span>
                </button>

                <button
                  className="fa__item"
                  onClick={() => {
                    if (!uid) {
                      setAuthAlertOpen(true);
                      return;
                    }
                    setReportOpen(true);
                  }}
                  disabled={busy}
                >
                  <img src={reportIcon} alt="" className="fa__ico" />
                  <span>Поскаржитись</span>
                </button>

                {showStaffForThisCard && (
                  <>
                    <div className="fa__div" />

                    <button
                      className="fa__item danger"
                      onClick={() => setReasonOpen(true)}
                      disabled={busy}
                      title={`Видалити як ${
                        staffRole === "admin" ? "адміністратор" : "модератор"
                      }`}
                    >
                      <img src={deleteIcon} alt="" className="fa__ico" />
                      <span>
                        Видалити (модератор{staffRole ? `: ${staffRole}` : ""})
                      </span>
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ReportsModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmitted={() => {
          setReportOpen(false);
          startLeave();
        }}
        userId={uid}
        targetType={entityType}
        targetId={id}
      />

      <ReasonModal
        open={reasonOpen}
        onClose={() => setReasonOpen(false)}
        onSubmit={submitModeration}
      />

      <Alerts
        open={authAlertOpen}
        variant="headlineLoginRequired"
        onCancel={() => setAuthAlertOpen(false)}
        onClose={() => setAuthAlertOpen(false)}
        onPrimary={() => {
          setAuthAlertOpen(false);

          const next =
            typeof window !== "undefined"
              ? encodeURIComponent(window.location.pathname + window.location.search)
              : "";

          if (typeof window !== "undefined") {
            window.location.href = `/auth?next=${next}`;
          }
        }}
      />

      <Alerts
        open={confirmOpen}
        variant={confirmVariant}
        onCancel={() => {
          setConfirmOpen(false);
          pendingActionRef.current = null;
        }}
        onClose={() => {
          setConfirmOpen(false);
          pendingActionRef.current = null;
        }}
        onPrimary={runConfirmedAction}
      />
    </>
  );
}